import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Autosave for a page someone types into and may leave at any moment: brew day and the recipe builder
 * (Tyler, 2026-10-01), done the way the invoice review does it (`pages/reviewAutosave.ts`). What the page
 * holds goes two places:
 *
 *   - this browser's own storage, on every change, so a killed browser or a dropped phone loses nothing
 *     on that device;
 *   - the server, half a second after typing stops and once more as the page is hidden, closed or left
 *     for another screen of the app, so any other browser picks up at the same point.
 *
 * The browser's copy is only what the server has not confirmed yet, and is cleared once it has. A page
 * opening finds it there only when something was typed and never reached the server.
 */

export type SaveStatus = "idle" | "saving" | "saved" | "retrying";

/** What a save came to: kept, worth trying again (offline, signed out), or refused for good. */
export type SaveResult = "saved" | "retry" | "rejected";

export interface Stamped<T> {
  value: T;
  savedAt: string;
}

const DEBOUNCE_MS = 500;
const RETRY_MS = 5000;

export function readLocal<T>(key: string): Stamped<T> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Stamped<T>) : null;
  } catch {
    return null;
  }
}

export function writeLocal<T>(key: string, stamped: Stamped<T>) {
  try {
    localStorage.setItem(key, JSON.stringify(stamped));
  } catch {
    // Storage full or private browsing: the server copy still goes.
  }
}

export function clearLocal(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}

const newKey = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/**
 * One autosave request: JSON to the API with an idempotency key, `keepalive` so that the save made as
 * the page closes still arrives. A reply refusing it for good (the thing is gone, or no longer takes
 * these changes) is "rejected"; no reply, signed out or a server error is worth trying again. A page
 * can narrow which replies count as refusals.
 */
export async function sendAutosave(
  method: "PUT",
  path: string,
  body: unknown,
  keepalive: boolean,
  refusals: readonly number[] = [400, 404, 409, 422],
): Promise<SaveResult> {
  try {
    const res = await fetch(`/api/v1${path}`, {
      method,
      keepalive,
      headers: { "content-type": "application/json", "idempotency-key": newKey() },
      body: JSON.stringify(body),
    });
    if (res.ok) return "saved";
    return refusals.includes(res.status) ? "rejected" : "retry";
  } catch {
    return "retry";
  }
}

export function useAutosave<T>(options: {
  /** Where this browser keeps what has not reached the server yet. */
  localKey: string;
  save: (stamped: Stamped<T>, keepalive: boolean) => Promise<SaveResult>;
  /** The server refused it for good: whatever was waiting is dropped. */
  onRejected?: () => void;
}) {
  const { localKey } = options;
  const save = useRef(options.save);
  save.current = options.save;
  const onRejected = useRef(options.onRejected);
  onRejected.current = options.onRejected;
  const pending = useRef<Stamped<T> | null>(null);
  const inflight = useRef<Promise<void> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mounted = useRef(true);
  const [status, setStatus] = useState<SaveStatus>("idle");

  const flush = useCallback(
    async (keepalive = false): Promise<void> => {
      clearTimeout(timer.current);
      const stamped = pending.current;
      if (!stamped) return;
      const run = (async () => {
        const result = await save.current(stamped, keepalive);
        // Only settled if nothing newer was typed while it was on its way.
        if (pending.current !== stamped) return;
        if (result === "saved") {
          pending.current = null;
          clearLocal(localKey);
          setStatus("saved");
        } else if (result === "rejected") {
          pending.current = null;
          clearLocal(localKey);
          setStatus("idle");
          onRejected.current?.();
        } else {
          setStatus("retrying");
          // Left behind, the copy in this browser waits for the page to be opened again.
          if (mounted.current) timer.current = setTimeout(() => void flush(), RETRY_MS);
        }
      })();
      inflight.current = run;
      await run;
    },
    [localKey],
  );

  const update = useCallback(
    (value: T) => {
      const stamped = { value, savedAt: new Date().toISOString() };
      pending.current = stamped;
      writeLocal(localKey, stamped);
      setStatus("saving");
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [localKey, flush],
  );

  /**
   * Stops what is waiting and waits out any save already on its way, before a deliberate save or a
   * discard that has to land last.
   */
  const settle = useCallback(async () => {
    clearTimeout(timer.current);
    pending.current = null;
    await inflight.current;
    clearLocal(localKey);
    setStatus("idle");
  }, [localKey]);

  // Leaving (the tab hidden, the page closed, or just another screen of the app) sends whatever is
  // still waiting, and the tab closing is exactly when a normal request would be cut off.
  useEffect(() => {
    mounted.current = true;
    const away = () => {
      if (document.visibilityState === "hidden") void flush(true);
    };
    const gone = () => void flush(true);
    document.addEventListener("visibilitychange", away);
    window.addEventListener("pagehide", gone);
    return () => {
      document.removeEventListener("visibilitychange", away);
      window.removeEventListener("pagehide", gone);
      mounted.current = false;
      void flush(true);
    };
  }, [flush]);

  return { update, flush, settle, status };
}

/** What the page says about saving, where the invoice review says it. */
export function saveStatusText(status: SaveStatus, idle = "Saves as you type"): string {
  switch (status) {
    case "saving":
      return "Saving…";
    case "saved":
      return "All changes saved";
    case "retrying":
      return "Not saved yet: kept on this device, trying again";
    default:
      return idle;
  }
}
